"use client";

import { c as _c } from "react/compiler-runtime";
import { create } from "zustand";
import { fetchBlockchainStatus } from "./api";
import { RECHECK_MS, UNREACHABLE, readBlockchainStatus, type ChainStatus } from "./status";

/**
 * The chain's status for this tab: asked once when the app loads (the site-wide notice asks),
 * again only when the tab comes back after the backend's five-minute cache has turned over.
 * Until the first answer the chain counts as up — the usual case — so nothing flickers off.
 */
type State = ChainStatus & {
  /** `null` until the first answer. */
  checkedAt: number | null;
  inFlight: boolean;
  check: (force?: boolean) => Promise<void>;
};
export const useChainStatus = create<State>()((set, get) => ({
  available: true,
  message: null,
  checkedAt: null,
  inFlight: false,
  check: async (force = false) => {
    const {
      checkedAt,
      inFlight
    } = get();
    if (inFlight || !force && checkedAt != null && Date.now() - checkedAt < RECHECK_MS) return;
    set({
      inFlight: true
    });
    let next: ChainStatus;
    try {
      next = readBlockchainStatus(await fetchBlockchainStatus());
    } catch {
      next = UNREACHABLE;
    }
    set({
      ...next,
      checkedAt: Date.now(),
      inFlight: false
    });
  }
}));

/** Is a blockchain action off right now, and the backend's words for why (in the page's language). */
export function useChainDown(lang) {
  const $ = _c(3);
  const available = useChainStatus(_temp);
  const message = useChainStatus(_temp2);
  const t0 = !available;
  const t1 = message ? message[lang] : null;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      down: t0,
      message: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp2(s_0) {
  return s_0.message;
}
function _temp(s) {
  return s.available;
}